import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/**
 * "Instalment due" popup state — the EMI sibling of `followUpNotificationSlice`.
 *
 * SERVER-DRIVEN, exactly like the follow-up popup, and for the same reason: the
 * due moment belongs to the backend sweep, which pushes `installment:due` over
 * the websocket to the deal's personal-scope owners. Do not reintroduce
 * client-side scheduling here either — a setInterval can only ever fire for the
 * person who happens to have the tab open.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ONE THING THAT COULD NOT BE COPIED FROM THE FOLLOW-UP SLICE
 *
 * That slice dedupes on a bare row id, which is right there: a follow-up rings
 * exactly ONCE, so "have I seen id 42?" is the whole question.
 *
 * An instalment rings up to THREE times — N days before, on the day, and then
 * once every day it stays overdue. Deduping on `installmentId` alone would show
 * the pre-reminder and then silently swallow the due-day alert and every
 * overdue nudge after it, for the rest of the session, with no error anywhere.
 * The reminder people most need is the one that would vanish.
 *
 * So the key is `reminderKey` = `<installmentId>:<arm>:<IST date>`, minted by
 * the server. Distinct per ring, stable across a socket reconnect.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** Which of the three sweep arms produced this alert. */
export type InstallmentReminderArm = "pre" | "due" | "overdue";

export interface DueInstallment {
  /**
   * Server-minted `<installmentId>:<arm>:<YYYY-MM-DD IST>`. THE dedupe key.
   * Never substitute the bare installment id — see the header.
   */
  reminderKey: string;
  installmentId: number;
  planId: number;
  leadId: string | number;
  childId: string | number;
  arm: InstallmentReminderArm;

  /** Identity, for the popup header and its call / WhatsApp buttons. */
  leadName: string;
  leadInitials: string;
  phone?: string | null;
  phoneCountryCode?: string | null;
  /** Preferred over `phone` for the WhatsApp link when the lead has one. */
  whatsapp?: string | null;
  stage?: string | null;
  dealName?: string | null;

  /** This instalment. */
  seq: number;
  ofCount: number;
  amount: number;
  paidAmount: number;
  outstanding: number;
  /** ISO — the IST calendar day it fell due. */
  dueDate: string;
  /** Set on the `overdue` arm. */
  daysOverdue?: number;
  /** Set on the `pre` arm. */
  daysUntilDue?: number;

  /** Plan progress, for the strip under the amount. */
  planPaidCount: number;
  planTotalCount: number;
  planPaidAmount: number;
  planTotalAmount: number;

  /**
   * How many OTHER instalments came due for this user in the same server tick.
   * The sweep coalesces per recipient — one alert naming the most urgent — so
   * this is the honest "+N more". It is NOT `queue.length`, which only counts
   * separate ticks; the popup adds the two together.
   */
  alsoDueCount?: number;
}

export interface InstallmentNotificationState {
  queue: DueInstallment[];
  active: DueInstallment | null;
  /** Reminder keys already shown this session — a reconnect must not re-pop. */
  seen: string[];
}

const initialState: InstallmentNotificationState = {
  queue: [],
  active: null,
  seen: [],
};

/**
 * Cap on `seen`, matching the follow-up slice.
 *
 * Safe here only BECAUSE the server coalesces to one alert per recipient per
 * tick: the keys that reach this array are alerts, not instalments, so a
 * counsellor chasing 300 overdue rows still adds a handful of entries a day. If
 * the fan-out ever stops coalescing, this cap starts silently re-popping the
 * oldest alerts and needs revisiting.
 */
const SEEN_CAP = 200;

function promoteNext(state: InstallmentNotificationState) {
  if (state.active) return;
  if (state.queue.length > 0) {
    state.active = state.queue[0] as DueInstallment;
    state.queue = state.queue.slice(1);
  }
}

/**
 * Most urgent first, so the popup names the right one when a tick carries
 * several: overdue beats due-today beats pre-reminder, then by due date.
 * A pre-reminder must never jump the queue ahead of money already late.
 */
const ARM_RANK: Record<InstallmentReminderArm, number> = {
  overdue: 0,
  due: 1,
  pre: 2,
};

function sortQueue(state: InstallmentNotificationState) {
  state.queue.sort((a, b) => {
    const byArm = ARM_RANK[a.arm] - ARM_RANK[b.arm];
    if (byArm !== 0) return byArm;
    return a.dueDate.localeCompare(b.dueDate);
  });
}

const installmentNotificationSlice = createSlice({
  name: "installmentNotification",
  initialState,
  reducers: {
    /** An `installment:due` socket push landed. */
    pushDueInstallment(state, action: PayloadAction<DueInstallment>) {
      const incoming = action.payload;
      if (!incoming?.reminderKey) return;
      if (state.seen.includes(incoming.reminderKey)) return;
      if (state.active?.reminderKey === incoming.reminderKey) return;
      if (state.queue.some((i) => i.reminderKey === incoming.reminderKey)) return;

      state.seen.push(incoming.reminderKey);
      if (state.seen.length > SEEN_CAP) {
        state.seen = state.seen.slice(-SEEN_CAP);
      }
      state.queue.push(incoming);
      sortQueue(state);
      promoteNext(state);
    },

    dismissInstallment(state) {
      state.active = null;
      promoteNext(state);
    },

    /**
     * Put an alert back after an action on it FAILED (a snooze whose POST was
     * rejected). Deliberately bypasses the `seen` dedupe: the key has been seen,
     * but the server has already claimed the row and will not re-send it, so
     * refusing to re-queue would lose the reminder outright.
     */
    requeueInstallment(state, action: PayloadAction<DueInstallment>) {
      const item = action.payload;
      if (!item?.reminderKey) return;
      if (state.active?.reminderKey === item.reminderKey) return;
      if (state.queue.some((i) => i.reminderKey === item.reminderKey)) return;
      state.queue.unshift(item);
      sortQueue(state);
      promoteNext(state);
    },

    /**
     * Drop every queued alert for one instalment — used when the user records a
     * payment straight from the popup, so its sibling arms don't pop afterwards
     * for money that has just landed.
     */
    clearInstallmentAlerts(state, action: PayloadAction<number>) {
      const id = action.payload;
      state.queue = state.queue.filter((i) => i.installmentId !== id);
      if (state.active?.installmentId === id) state.active = null;
      promoteNext(state);
    },

    clearActiveInstallment(state) {
      state.active = null;
      promoteNext(state);
    },
  },
});

export const {
  pushDueInstallment,
  dismissInstallment,
  requeueInstallment,
  clearInstallmentAlerts,
  clearActiveInstallment,
} = installmentNotificationSlice.actions;

export default installmentNotificationSlice.reducer;
